import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Checkbox } from '../../components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import MainLayout from '../../components/layout/MainLayout';
import { useTheme } from '../../contexts/ThemeContext';
import { 
  FileSignature,
  FileText,
  CheckCircle2,
  Clock,
  AlertCircle,
  Eye,
  Loader2,
  Calendar,
  Euro,
  Building2,
  Download
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/axios';

const ClientContractsPage = () => {
  const { theme } = useTheme();
  const [loading, setLoading] = useState(true);
  const [contracts, setContracts] = useState([]);
  const [pendingContracts, setPendingContracts] = useState([]);
  const [selectedContract, setSelectedContract] = useState(null);
  const [contractHtml, setContractHtml] = useState('');
  const [showContractModal, setShowContractModal] = useState(false);
  const [cgvAccepted, setCgvAccepted] = useState(false);
  const [signing, setSigning] = useState(false);

  useEffect(() => {
    fetchContracts();
  }, []);

  const fetchContracts = async () => {
    try {
      setLoading(true);
      const [allRes, pendingRes] = await Promise.all([
        api.get('/contracts/my'),
        api.get('/contracts/pending-signatures')
      ]);
      setContracts(allRes.data || []);
      setPendingContracts(pendingRes.data || []);
    } catch (error) {
      console.error('Error fetching contracts:', error);
      toast.error('Erreur lors du chargement des contrats');
    } finally {
      setLoading(false);
    }
  };

  const viewContract = async (contract) => {
    try {
      const res = await api.get(`/contracts/${contract.id}/html`);
      setContractHtml(res.data.html);
      setSelectedContract(res.data.contract);
      setCgvAccepted(false);
      setShowContractModal(true);
    } catch (error) {
      toast.error('Erreur lors du chargement du contrat');
    }
  };

  const signContract = async () => {
    if (!cgvAccepted) {
      toast.error('Veuillez accepter les CGV pour signer le contrat');
      return;
    }

    setSigning(true);
    try {
      await api.post(`/contracts/${selectedContract.id}/client-sign`, {
        cgv_accepted: true
      });
      toast.success('Contrat signé avec succès !');
      setShowContractModal(false);
      fetchContracts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la signature');
    } finally {
      setSigning(false);
    }
  };

  const getStatusBadge = (contract) => {
    if (contract.status === 'pending_signature' || !contract.client_signed) {
      return <Badge className="bg-amber-100 text-amber-700">En attente de signature</Badge>;
    }
    if (contract.status === 'active') {
      return <Badge className="bg-emerald-100 text-emerald-700">Actif</Badge>;
    }
    if (contract.status === 'cancelled') {
      return <Badge className="bg-red-100 text-red-700">Annulé</Badge>;
    }
    return <Badge variant="outline">{contract.status}</Badge>;
  };

  const getContractTypeLabel = (type) => {
    const labels = {
      'hosting': 'Hébergement',
      'maintenance': 'Maintenance',
      'both': 'Hébergement & Maintenance',
      'services': 'Services'
    };
    return labels[type] || type;
  };

  if (loading) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold" style={{ color: theme.colors?.primary }}>
            Mes contrats
          </h1>
          <p className="text-slate-500">Consultez et signez vos contrats de prestation</p>
        </div>

        {/* Pending Signatures Alert */}
        {pendingContracts.length > 0 && (
          <Card className="border-amber-300 bg-amber-50">
            <CardContent className="pt-6">
              <div className="flex items-start gap-4">
                <div className="p-3 bg-amber-100 rounded-full">
                  <FileSignature className="w-6 h-6 text-amber-600" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-amber-900">
                    {pendingContracts.length} contrat{pendingContracts.length > 1 ? 's' : ''} en attente de signature
                  </h3>
                  <p className="text-amber-700 text-sm mt-1">
                    Veuillez signer les contrats ci-dessous pour activer vos prestations.
                  </p>
                  <div className="flex flex-wrap gap-2 mt-3">
                    {pendingContracts.map(contract => (
                      <Button
                        key={contract.id}
                        size="sm"
                        className="bg-amber-600 hover:bg-amber-700 text-white"
                        onClick={() => viewContract(contract)}
                      >
                        <FileSignature className="w-4 h-4 mr-1" />
                        Signer "{contract.name}"
                      </Button>
                    ))}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Active Contracts */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5" style={{ color: theme.colors?.accent }} />
              Tous mes contrats
            </CardTitle>
            <CardDescription>
              {contracts.length} contrat{contracts.length > 1 ? 's' : ''}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {contracts.length === 0 ? (
              <div className="text-center py-12 text-slate-500">
                <FileText className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p>Aucun contrat pour le moment</p>
              </div>
            ) : (
              <div className="space-y-4">
                {contracts.map(contract => (
                  <div 
                    key={contract.id}
                    className="p-4 border rounded-xl hover:shadow-md transition-shadow"
                  >
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className="font-semibold truncate">{contract.name}</h3>
                          {getStatusBadge(contract)}
                        </div>
                        <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-sm text-slate-500">
                          <span className="flex items-center gap-1">
                            <Building2 className="w-4 h-4" />
                            {getContractTypeLabel(contract.contract_type)}
                          </span>
                          <span className="flex items-center gap-1">
                            <Euro className="w-4 h-4" />
                            {contract.monthly_price}€/mois
                          </span>
                          {contract.hours_included > 0 && (
                            <span className="flex items-center gap-1">
                              <Clock className="w-4 h-4" />
                              {contract.hours_included}h incluses
                            </span>
                          )}
                          <span className="flex items-center gap-1">
                            <Calendar className="w-4 h-4" />
                            {contract.engagement}
                          </span>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => viewContract(contract)}
                        >
                          <Eye className="w-4 h-4 mr-1" />
                          Voir
                        </Button>
                        {!contract.client_signed && (
                          <Button
                            size="sm"
                            className="bg-amber-500 hover:bg-amber-600 text-white"
                            onClick={() => viewContract(contract)}
                          >
                            <FileSignature className="w-4 h-4 mr-1" />
                            Signer
                          </Button>
                        )}
                      </div>
                    </div>
                    
                    {/* Signature status */}
                    <div className="flex gap-4 mt-3 pt-3 border-t text-xs">
                      <div className="flex items-center gap-1">
                        {contract.agency_signed ? (
                          <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                        ) : (
                          <Clock className="w-4 h-4 text-amber-500" />
                        )}
                        <span className="text-slate-600">
                          Prestataire : {contract.agency_signed ? 'Signé' : 'En attente'}
                        </span>
                      </div>
                      <div className="flex items-center gap-1">
                        {contract.client_signed ? (
                          <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                        ) : (
                          <Clock className="w-4 h-4 text-amber-500" />
                        )}
                        <span className="text-slate-600">
                          Client : {contract.client_signed ? 'Signé' : 'En attente'}
                        </span>
                      </div>
                      {contract.cgv_accepted && (
                        <div className="flex items-center gap-1">
                          <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                          <span className="text-slate-600">CGV acceptées</span>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Contract View/Sign Modal */}
        <Dialog open={showContractModal} onOpenChange={setShowContractModal}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
            <DialogHeader>
              <DialogTitle style={{ color: theme.colors?.primary }}>
                {selectedContract?.client_signed ? 'Contrat signé' : 'Signature du contrat'}
              </DialogTitle>
              <DialogDescription>
                {selectedContract?.name}
              </DialogDescription>
            </DialogHeader>
            
            {/* Contract HTML Preview */}
            <div 
              className="flex-1 overflow-y-auto border rounded-lg bg-white p-4"
              dangerouslySetInnerHTML={{ __html: contractHtml }}
            />
            
            {/* Signature section - only if not signed */}
            {selectedContract && !selectedContract.client_signed && (
              <div className="pt-4 border-t space-y-4">
                <div className="flex items-start gap-3 p-4 bg-slate-50 rounded-lg">
                  <Checkbox
                    id="cgv"
                    checked={cgvAccepted}
                    onCheckedChange={setCgvAccepted}
                    className="mt-1"
                  />
                  <label htmlFor="cgv" className="text-sm cursor-pointer">
                    <span className="font-medium">J'accepte les Conditions Générales de Vente</span>
                    <p className="text-slate-500 mt-1">
                      En cochant cette case, je reconnais avoir lu et accepté les CGV du prestataire 
                      et je m'engage à respecter les termes du présent contrat.
                    </p>
                  </label>
                </div>
                
                <DialogFooter>
                  <Button
                    variant="outline"
                    onClick={() => setShowContractModal(false)}
                  >
                    Annuler
                  </Button>
                  <Button
                    onClick={signContract}
                    disabled={!cgvAccepted || signing}
                    className="bg-emerald-500 hover:bg-emerald-600"
                  >
                    {signing ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Signature...
                      </>
                    ) : (
                      <>
                        <FileSignature className="w-4 h-4 mr-2" />
                        Signer le contrat
                      </>
                    )}
                  </Button>
                </DialogFooter>
              </div>
            )}
            
            {/* Already signed - just show close button */}
            {selectedContract && selectedContract.client_signed && (
              <DialogFooter>
                <Button onClick={() => setShowContractModal(false)}>
                  Fermer
                </Button>
              </DialogFooter>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </MainLayout>
  );
};

export default ClientContractsPage;
